/** Small pieces the list page, the agent's Add dialogs and the agent's connector rows share. */

import { useState, type ReactNode } from 'react';
import { Popover } from 'antd';
import { SharingHelpContent } from './SharingTexts';
import { useLanguage } from '@hooks/useLanguage';
import { serviceIconFor, type ServiceIdentity } from '../utils/serviceIcons';
import { Field } from '@modules/chatbots/components/ConnectorDialog';
import type { LibraryCredential, LibraryKind } from '../services/connectorLibraryService';

/** Protocol names — the same in every language. */
export const KIND_LABEL: Record<LibraryKind, string> = { mcp: 'MCP', api: 'API' };

/** The entry's first letter on the brand gradient — the card icon. */
export function EntryAvatar({ name }: { name: string }) {
  const letter = Array.from((name || '?').trim())[0]?.toUpperCase() || '?';
  return <span className="cxl-avatar" aria-hidden="true">{letter}</span>;
}

/**
 * The service's own mark on a white tile (readable on light and dark pages) when the
 * connector reaches a service we know (utils/serviceIcons) — otherwise `fallback`,
 * by default the letter avatar. Decorative: the name is always written beside it.
 */
export function ServiceIcon({ name, slug, url, fallback, className = 'cxl-avatar' }: ServiceIdentity & {
  fallback?: ReactNode;
  /** The tile's size class: the library's avatar, or the agent tab's `cxc__icon`. */
  className?: string;
}) {
  const icon = serviceIconFor({ name, slug, url });
  if (!icon) return <>{fallback ?? <EntryAvatar name={name || ''} />}</>;
  return (
    <span className={`${className} cx-service-icon`} aria-hidden="true" data-service={icon.key}>
      {icon.paths ? (
        <svg viewBox={icon.viewBox} focusable="false">
          {icon.paths.map((p, i) => (
            <path
              key={i}
              d={p.d}
              fill={p.fill}
              fillRule={p.fillRule}
              stroke={p.stroke}
              strokeWidth={p.strokeWidth}
              strokeMiterlimit={p.strokeMiterlimit}
              transform={p.transform}
            />
          ))}
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" focusable="false"><path d={icon.path} fill={icon.color} /></svg>
      )}
    </span>
  );
}

export function KindTag({ kind }: { kind: LibraryKind }) {
  return (
    <span className={`cxl-tag cxl-tag--${kind}`} dir="ltr">
      {KIND_LABEL[kind]}
    </span>
  );
}

/**
 * The three statuses of an agent's connector, explained once in plain words —
 * beside the Connectors tab's title and in the list page's header.
 */
export function StatusHelp() {
  const { t } = useLanguage();
  const content = (
    <div className="cxl-help" data-testid="connector-status-help-content">
      <p className="cxl-help__lead">{t('connectorLibrary.help.lead')}</p>
      <dl className="cxl-help__list">
        <dt><span className="cx-pill cx-pill--accent">{t('connectorLibrary.status.sharedShort')}</span></dt>
        <dd>{t('connectorLibrary.help.shared')}</dd>
        <dt><span className="cx-pill cxl-pill--customized">{t('connectorLibrary.status.customized')}</span></dt>
        <dd>{t('connectorLibrary.help.customized')}</dd>
        <dt><span className="cx-pill">{t('connectorLibrary.status.own')}</span></dt>
        <dd>{t('connectorLibrary.help.own')}</dd>
      </dl>
    </div>
  );
  return (
    <Popover content={content} trigger={['click']} title={t('connectorLibrary.help.title')}>
      <button type="button" className="cxl-help__button" aria-label={t('connectorLibrary.help.title')}
              data-testid="connector-status-help">
        <InfoGlyph />
      </button>
    </Popover>
  );
}

/**
 * "How sharing works" — the list page's rules, behind a link in the subtitle;
 * short first, the rest behind "More" (item 80b, SharingTexts.tsx). It opens
 * collapsed every time.
 */
export function SharingHelp() {
  const { t } = useLanguage();
  const [more, setMore] = useState(false);
  return (
    <Popover content={<SharingHelpContent more={more} onMore={() => setMore(m => !m)} />} trigger={['click']}
             title={t('connectorLibrary.sharing.title')} placement="bottom"
             onOpenChange={open => { if (!open) setMore(false); }}>
      <button type="button" className="cxl-help__link" data-testid="connector-sharing-help">
        {t('connectorLibrary.sharing.link')}
      </button>
    </Popover>
  );
}

/** Whether what was typed is enough to send. */
export function credentialComplete(entry: { kind: LibraryKind; auth_type?: string | null }, value: LibraryCredential): boolean {
  if (entry.kind === 'api' && entry.auth_type === 'basic') return !!value.username?.trim() && !!value.password;
  return !!value.value?.trim();
}

/** The shape of the secret a connector asks for — never a value. */
export interface CredentialShape {
  kind: LibraryKind;
  auth_header_name?: string | null;
  auth_type?: string | null;
  auth_meta?: { name?: string } | null;
}

/** The secret an entry asks for: a header value (MCP), a key / token, or a username and password (API). */
export function CredentialFields({ entry, value, onChange }: {
  entry: CredentialShape;
  value: LibraryCredential;
  onChange: (next: LibraryCredential) => void;
}) {
  const { t } = useLanguage();
  if (entry.kind === 'api' && entry.auth_type === 'basic') {
    return (
      <div className="cxd__row cxd__row--halves">
        <input className="form-input" dir="ltr" autoComplete="off" value={value.username ?? ''}
               placeholder={t('bots.connectors.api.auth.username')} aria-label={t('bots.connectors.api.auth.username')}
               onChange={e => onChange({ ...value, username: e.target.value })} data-testid="connector-library-credential-username" />
        <input type="password" className="form-input" dir="ltr" autoComplete="new-password" value={value.password ?? ''}
               placeholder={t('bots.connectors.api.auth.password')} aria-label={t('bots.connectors.api.auth.password')}
               onChange={e => onChange({ ...value, password: e.target.value })} data-testid="connector-library-credential-password" />
      </div>
    );
  }
  const label = entry.kind === 'mcp' ? entry.auth_header_name : entry.auth_meta?.name;
  return (
    <Field label={label ? <code dir="ltr">{label}</code> : t('bots.connectors.api.auth.valuePlaceholder')}>
      <input type="password" className="form-input form-input--mono" dir="ltr" autoComplete="new-password"
             value={value.value ?? ''} placeholder={t('bots.connectors.api.auth.valuePlaceholder')}
             aria-label={t('bots.connectors.api.auth.valuePlaceholder')}
             onChange={e => onChange({ ...value, value: e.target.value })} data-testid="connector-library-credential-value" />
    </Field>
  );
}

export function LibraryGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M4 5h4v14H4z" />
      <path d="M10 5h4v14h-4z" />
      <path d="m16.5 5.5 3.8-1 3 13.6-3.8 1z" />
    </svg>
  );
}

export function MoreGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
    </svg>
  );
}

export function InfoGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 7.5h.01" />
    </svg>
  );
}

export function WarnGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

export function CloseGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
